import { useState, useEffect, useMemo } from 'react';
import { View, Pressable, Alert, Platform, useWindowDimensions, ScrollView, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AppImage } from '@/components/ui/AppImage';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { 
  subscribeToSavedRecommendations,
  unsaveRecommendation,
  type SavedRecommendation
} from '@/services/recommendations';
import { createTrip, type CreateTripData } from '@/services/trips';
import { useUserStore } from '@/state/userStore';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { hasPremiumAccess } from '@/services/entitlements';
import { ShareRecommendationModal } from './ShareRecommendationModal';
import { useLandmarkImage } from '@/hooks/useLandmarkImage';

type SavedRecommendationsListProps = {
  userId: string;
  /** When false, free users cannot start another active trip (from parent trips list — avoids stale cache). */
  canCreateNewActiveTrip: boolean;
  maxItems?: number;
  onSelectRecommendation?: (recommendation: SavedRecommendation) => void;
  onCreateTripRecommendation?: (
    recommendation: SavedRecommendation,
    startDate: Date,
    endDate: Date,
  ) => Promise<string | null>;
  showCreateTripButton?: boolean;
  showShareButton?: boolean;
  showRemoveButton?: boolean;
  onEmptyAskAi?: () => void;
  onEmptyBrowseTemplates?: () => void;
  layout?: 'vertical' | 'horizontal';
  copyVariant?: 'recommendations' | 'places';
};

type RecommendationCardProps = {
  recommendation: SavedRecommendation;
  theme: any;
  userId: string;
  onSelectRecommendation?: (recommendation: SavedRecommendation) => void;
  showCreateTripButton?: boolean;
  showShareButton?: boolean;
  showRemoveButton?: boolean;
  onRemove: (recommendation: SavedRecommendation) => void;
  onCreateTrip: (recommendation: SavedRecommendation) => void;
  creatingTrip: string | null;
};

function RecommendationCard({
  recommendation,
  theme,
  userId,
  onSelectRecommendation,
  showCreateTripButton,
  showShareButton,
  showRemoveButton,
  onRemove,
  onCreateTrip,
  creatingTrip,
}: RecommendationCardProps) {
  const isTemplate = recommendation.source === 'template' && Boolean(recommendation.templateId);
  const displayTitle = recommendation.title || recommendation.destination;
  const { imageUrl, isLoading: imageLoading } = useLandmarkImage(recommendation.destination);
  const [shareModalVisible, setShareModalVisible] = useState(false);

  const handleShare = (rec: SavedRecommendation) => {
    setShareModalVisible(true);
  };

  const budgetLabel = useMemo((): string | null => {
    if (typeof recommendation.budgetMin === 'number' && typeof recommendation.budgetMax === 'number') {
      const currency = recommendation.budgetCurrency || 'USD';
      try {
        const fmt = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          notation: 'compact',
          maximumFractionDigits: 1,
        });
        return `${fmt.format(recommendation.budgetMin)}–${fmt.format(recommendation.budgetMax)}`;
      } catch {
        return `$${recommendation.budgetMin.toLocaleString()}–$${recommendation.budgetMax.toLocaleString()}`;
      }
    }
    if (recommendation.estimatedBudget) {
      return `$${recommendation.estimatedBudget.toLocaleString()}`;
    }
    return null;
  }, [recommendation.budgetCurrency, recommendation.budgetMax, recommendation.budgetMin, recommendation.estimatedBudget]);

  const perTravelerBreakdownEntries = useMemo(() => {
    if (!recommendation.perTravelerCosts || recommendation.perTravelerCosts.total <= 0) {
      return [];
    }
    const breakdown = recommendation.perTravelerCosts.breakdown;
    return [
      { label: 'Flight:', value: breakdown.transportation },
      {
        label: `Accommodation${recommendation.duration ? ` (${recommendation.duration} days)` : ''}:`,
        value: breakdown.accommodation,
      },
      { label: 'Activities:', value: breakdown.activities },
      { label: 'Food:', value: breakdown.food },
      { label: 'Other:', value: breakdown.other },
    ].filter((entry) => typeof entry.value === 'number' && entry.value > 0) as Array<{
      label: string;
      value: number;
    }>;
  }, [recommendation.duration, recommendation.perTravelerCosts]);

  return (
    <>
      <Card variant="outlined">
        <View style={{ gap: theme.spacing(1.5) }}>
          {/* Image and Header Row */}
          <View style={{ flexDirection: 'row', gap: theme.spacing(1.5) }}>
            {recommendation.coverImage ? (
              <AppImage
                source={{ uri: recommendation.coverImage }}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.borderMuted,
                }}
                resizeMode="cover"
                cachePolicy="memory-disk"
                accessibilityLabel={`Cover image for ${displayTitle}`}
              />
            ) : imageUrl ? (
              <AppImage
                source={{ uri: imageUrl }}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.borderMuted,
                }}
                resizeMode="cover"
                cachePolicy="memory-disk"
                accessibilityLabel={`Destination image for ${displayTitle}`}
              />
            ) : null}
            {!imageUrl && !imageLoading && (
              <View
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.borderMuted,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Ionicons name="image-outline" size={32} color={theme.colors.textSecondary} />
              </View>
            )}
            <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <SubHeading style={{ flex: 1 }} numberOfLines={2}>
                  {displayTitle}
                </SubHeading>
                {budgetLabel && (
                  <Caption color={theme.colors.primary} style={{ fontWeight: '700' }} numberOfLines={1}>
                    {budgetLabel}
                  </Caption>
                )}
              </View>
              {recommendation.title && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                  <Ionicons name="location-outline" size={16} color={theme.colors.textSecondary} />
                  <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                    {recommendation.destination}
                  </Caption>
                </View>
              )}
            </View>
          </View>

          {recommendation.dates && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="calendar-outline" size={16} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>{recommendation.dates}</Caption>
            </View>
          )}

          {recommendation.description && (
            <Body style={{ lineHeight: 20 }}>{recommendation.description}</Body>
          )}

          {/* Cost Breakdown Display */}
          {(recommendation.estimatedBudget || recommendation.costBreakdown || recommendation.perTravelerCosts) && (
            <View
              style={{
                backgroundColor: theme.colors.borderMuted,
                padding: theme.spacing(1.5),
                borderRadius: theme.radii.md,
                gap: theme.spacing(1),
              }}
            >
              <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                Cost Breakdown
              </Caption>
              
              {recommendation.numberOfTravelers && recommendation.numberOfTravelers > 1 && (
                <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
                  For {recommendation.numberOfTravelers} travelers
                </Caption>
              )}

              {/* Show per-person cost breakdown as primary display */}
              {recommendation.perTravelerCosts && recommendation.perTravelerCosts.total > 0 ? (
                <View style={{ gap: theme.spacing(0.75) }}>
                  <Caption color={theme.colors.textSecondary} style={{ fontWeight: '600', fontSize: 14 }}>
                    Total trip cost: ${recommendation.perTravelerCosts.total.toLocaleString()}/person
                  </Caption>
                  {perTravelerBreakdownEntries.length > 0 && (
                    <View style={{ gap: theme.spacing(0.5), marginTop: theme.spacing(0.5) }}>
                      {perTravelerBreakdownEntries.map((entry) => (
                        <View key={entry.label} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                          <Caption color={theme.colors.textSecondary}>{entry.label}</Caption>
                          <Caption color={theme.colors.text}>${entry.value.toLocaleString()}</Caption>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              ) : recommendation.estimatedBudget ? (
                <View style={{ marginBottom: theme.spacing(0.5) }}>
                  <Caption color={theme.colors.textSecondary} style={{ fontWeight: '600' }}>
                    {recommendation.numberOfTravelers && recommendation.numberOfTravelers > 1 
                      ? `Total Trip Cost (for ${recommendation.numberOfTravelers} travelers): $${recommendation.estimatedBudget.toLocaleString()}`
                      : `Total Trip Cost: $${recommendation.estimatedBudget.toLocaleString()}`}
                  </Caption>
                </View>
              ) : null}
            </View>
          )}

          {recommendation.whyThisMatch && (
            <View
              style={{
                backgroundColor: theme.colors.borderMuted,
                padding: theme.spacing(1.5),
                borderRadius: theme.radii.md,
                gap: theme.spacing(0.5),
              }}
            >
              <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                Why this matches:
              </Caption>
              <Caption color={theme.colors.textSecondary}>{recommendation.whyThisMatch}</Caption>
            </View>
          )}

          {(recommendation.sharedWith?.length ?? 0) > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="people-outline" size={16} color={theme.colors.primary} />
              <Caption color={theme.colors.primary}>
                Shared with {recommendation.sharedWith!.length}{' '}
                {recommendation.sharedWith!.length === 1 ? 'companion' : 'companions'}
              </Caption>
            </View>
          )}

          <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(0.5) }}>
            {showCreateTripButton && (
              <Button
                label={isTemplate ? 'Use template' : creatingTrip === recommendation.id ? 'Creating...' : 'Create trip'}
                onPress={() => onCreateTrip(recommendation)}
                disabled={creatingTrip === recommendation.id}
                style={{ flex: 1 }}
                labelNumberOfLines={1}
              />
            )}
            {showShareButton && (
              <Pressable
                onPress={() => handleShare(recommendation)}
                style={({ pressed }) => ({
                  padding: theme.spacing(1.5),
                  borderRadius: theme.radii.md,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                  backgroundColor: theme.colors.surface,
                  opacity: pressed ? 0.7 : 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                })}
              >
                <Ionicons 
                  name={(recommendation.sharedWith?.length ?? 0) > 0 ? 'share-social' : 'share-outline'} 
                  size={20} 
                  color={theme.colors.primary} 
                />
              </Pressable>
            )}
            {showRemoveButton && (
              <Pressable
                onPress={() => onRemove(recommendation)}
                style={({ pressed }) => ({
                  padding: theme.spacing(1.5),
                  borderRadius: theme.radii.md,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                  backgroundColor: theme.colors.surface,
                  opacity: pressed ? 0.7 : 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                })}
              >
                <Ionicons name="trash-outline" size={20} color={theme.colors.error || theme.colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      </Card>

      {shareModalVisible && (
        <ShareRecommendationModal
          visible={shareModalVisible}
          onClose={() => setShareModalVisible(false)}
          userId={userId}
          recommendation={recommendation}
        />
      )}
    </>
  );
}

type CompactRecommendationCardProps = {
  recommendation: SavedRecommendation;
  theme: any;
  userId: string;
  onSelectRecommendation?: (recommendation: SavedRecommendation) => void;
  showCreateTripButton?: boolean;
  showShareButton?: boolean;
  showRemoveButton?: boolean;
  onRemove: (recommendation: SavedRecommendation) => void;
  onCreateTrip: (recommendation: SavedRecommendation) => void;
  onShare: (recommendation: SavedRecommendation) => void;
  creatingTrip: string | null;
};

function CompactRecommendationCard({
  recommendation,
  theme,
  onSelectRecommendation,
  showCreateTripButton,
  showShareButton,
  showRemoveButton,
  onRemove,
  onCreateTrip,
  onShare,
  creatingTrip,
}: CompactRecommendationCardProps) {
  const isTemplate = recommendation.source === 'template' && Boolean(recommendation.templateId);
  const rawTitle = typeof recommendation.title === 'string' ? recommendation.title.trim() : '';
  const destination = typeof recommendation.destination === 'string' ? recommendation.destination.trim() : '';
  const displayTitle = rawTitle || destination;
  const { imageUrl, isLoading: imageLoading } = useLandmarkImage(recommendation.destination);
  const displayImage = recommendation.coverImage || imageUrl;
  const budgetLabel = useMemo(() => {
    // Prefer template ranges when available.
    if (typeof recommendation.budgetMin === 'number' && typeof recommendation.budgetMax === 'number') {
      const currency = recommendation.budgetCurrency || 'USD';
      const min = Math.round(recommendation.budgetMin);
      const max = Math.round(recommendation.budgetMax);
      try {
        const fmt = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          notation: 'compact',
          maximumFractionDigits: 1,
        });
        return `${fmt.format(min)}–${fmt.format(max)}`;
      } catch {
        return `$${min.toLocaleString()}–$${max.toLocaleString()}`;
      }
    }

    // Otherwise fall back to estimated budget (AI recommendations often use this).
    if (typeof recommendation.estimatedBudget === 'number') {
      const value = Math.round(recommendation.estimatedBudget);
      try {
        const fmt = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: recommendation.budgetCurrency || 'USD',
          currencyDisplay: 'narrowSymbol',
          notation: 'compact',
          maximumFractionDigits: 1,
        });
        return fmt.format(value);
      } catch {
        return `$${value.toLocaleString()}`;
      }
    }

    return null;
  }, [recommendation.budgetCurrency, recommendation.budgetMax, recommendation.budgetMin, recommendation.estimatedBudget]);

  const showLocationRow =
    !!destination && !!rawTitle && rawTitle.toLowerCase() !== destination.toLowerCase();

  const chipBg =
    theme.colorScheme === 'dark'
      ? 'rgba(30,30,30,0.88)'
      : 'rgba(255,255,255,0.92)';

  const isCreating = creatingTrip === recommendation.id;

  return (
    <Pressable
      onPress={() => onSelectRecommendation?.(recommendation)}
      android_ripple={{ color: theme.colors.overlay }}
      style={({ pressed }) => [
        {
          opacity: pressed ? 0.92 : 1,
        },
      ]}
    >
      <Card
        variant="elevated"
        elevation={1}
        padding={0}
        style={{
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: theme.colors.borderMuted,
          width: 210,
        }}
      >
        <View
          style={{
            height: 110,
            width: '100%',
            backgroundColor: theme.colors.borderMuted,
            position: 'relative',
          }}
        >
          {displayImage ? (
            <AppImage
              source={{ uri: displayImage }}
              style={{ width: '100%', height: '100%' }}
              resizeMode="cover"
              cachePolicy="memory-disk"
              accessibilityLabel={`Image for ${displayTitle}`}
            />
          ) : (
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              {imageLoading ? (
                <ActivityIndicator size="small" color={theme.colors.primary} />
              ) : (
                <Ionicons name="image-outline" size={28} color={theme.colors.textSecondary} />
              )}
            </View>
          )}

          {/* Top right quick actions */}
          {(showShareButton || showRemoveButton) && (
            <View
              style={{
                position: 'absolute',
                top: theme.spacing(1),
                right: theme.spacing(1),
                flexDirection: 'row',
                gap: theme.spacing(0.5),
              }}
            >
              {showShareButton && (
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation?.();
                    onShare(recommendation);
                  }}
                  hitSlop={8}
                  style={({ pressed }) => [
                    {
                      width: 32,
                      height: 32,
                      borderRadius: theme.radii.pill,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: pressed ? theme.colors.overlay : chipBg,
                      borderWidth: 1,
                      borderColor: theme.colors.borderMuted,
                    },
                  ]}
                >
                  <Ionicons name={(recommendation.sharedWith?.length ?? 0) > 0 ? 'share-social' : 'share-outline'} size={16} color={theme.colors.primary} />
                </Pressable>
              )}
              {showRemoveButton && (
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation?.();
                    onRemove(recommendation);
                  }}
                  hitSlop={8}
                  style={({ pressed }) => [
                    {
                      width: 32,
                      height: 32,
                      borderRadius: theme.radii.pill,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: pressed ? theme.colors.overlay : chipBg,
                      borderWidth: 1,
                      borderColor: theme.colors.borderMuted,
                    },
                  ]}
                >
                  <Ionicons name="trash-outline" size={16} color={theme.colors.error || theme.colors.textSecondary} />
                </Pressable>
              )}
            </View>
          )}
        </View>

        <View style={{ padding: theme.spacing(1.25), gap: theme.spacing(0.75) }}>
          <ThemedText variant="title" numberOfLines={2}>
            {displayTitle}
          </ThemedText>

          {showLocationRow ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ flex: 1 }}>
                {destination}
              </Caption>
            </View>
          ) : null}

          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
            {typeof recommendation.duration === 'number' && recommendation.duration > 0 ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="calendar-outline" size={14} color={theme.colors.textSecondary} />
                <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                  {recommendation.duration}d
                </Caption>
              </View>
            ) : (
              <View />
            )}

            {budgetLabel ? (
              <Caption color={theme.colors.primary} numberOfLines={1} style={{ fontWeight: '700' }}>
                {budgetLabel}
              </Caption>
            ) : (
              <View />
            )}
          </View>

          {showCreateTripButton && (
            <Pressable
              onPress={(e) => {
                e.stopPropagation?.();
                onCreateTrip(recommendation);
              }}
              disabled={isCreating}
              accessibilityRole="button"
              accessibilityLabel="Create trip"
              accessibilityHint="Creates a new trip from this saved recommendation"
              style={({ pressed }) => [
                {
                  marginTop: theme.spacing(0.25),
                  borderRadius: theme.radii.md,
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  backgroundColor: pressed ? theme.colors.overlay : theme.colors.surface,
                  paddingVertical: theme.spacing(0.75),
                  paddingHorizontal: theme.spacing(1),
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isCreating ? 0.7 : 1,
                },
              ]}
            >
              {isCreating ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                  <Caption color={theme.colors.primary} style={{ fontWeight: '700' }}>
                    Creating…
                  </Caption>
                </View>
              ) : (
                <Caption color={theme.colors.primary} style={{ fontWeight: '800' }}>
                  {isTemplate ? 'Use template' : 'Create trip'}
                </Caption>
              )}
            </Pressable>
          )}
        </View>
      </Card>
    </Pressable>
  );
}

export function SavedRecommendationsList({
  userId,
  canCreateNewActiveTrip,
  maxItems,
  onSelectRecommendation,
  onCreateTripRecommendation,
  showCreateTripButton = true,
  showShareButton = true,
  showRemoveButton = true,
  onEmptyAskAi,
  onEmptyBrowseTemplates,
  layout = 'vertical',
  copyVariant = 'recommendations',
}: SavedRecommendationsListProps) {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const isPremium = useUserStore((s) =>
    hasPremiumAccess({
      isPremium: s.profile?.isPremium,
      planTier: s.profile?.planTier,
      entitlement: s.profile?.entitlement,
    }),
  );
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  const [recommendations, setRecommendations] = useState<SavedRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState<SavedRecommendation | null>(null);
  const [creatingTrip, setCreatingTrip] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (__DEV__) {
      console.log('[SavedRecommendationsList] Setting up subscription');
    }
    setLoading(true);
    let subscriptionSettled = false;
    const loadingWatchdog = setTimeout(() => {
      if (!subscriptionSettled) {
        if (__DEV__) {
          console.warn('[SavedRecommendationsList] Subscription watchdog elapsed; clearing loading state');
        }
        setLoading(false);
      }
    }, 6000);

    const unsubscribe = subscribeToSavedRecommendations(userId, (savedRecommendations) => {
      subscriptionSettled = true;
      clearTimeout(loadingWatchdog);
      if (__DEV__) {
        console.log('[SavedRecommendationsList] Received recommendations:', savedRecommendations.length);
      }
      
      // Generate cost breakdowns if missing (frontend fallback)
      const recommendationsWithBreakdown = savedRecommendations.map((rec) => {
        // If breakdown already exists, use it
        if (rec.perTravelerCosts && rec.perTravelerCosts.total > 0) {
          return rec;
        }
        
        // Generate breakdown from estimatedBudget if missing
        if (rec.estimatedBudget && rec.estimatedBudget > 0) {
          const travelers = rec.numberOfTravelers || 1;
          const totalBudget = rec.estimatedBudget;
          
          // Generate estimated breakdown based on typical percentages
          const transportation = Math.round(totalBudget * 0.4);
          const accommodation = Math.round(totalBudget * 0.3);
          const activities = Math.round(totalBudget * 0.15);
          const food = Math.round(totalBudget * 0.1);
          const other = Math.round(totalBudget * 0.05);
          
          const costBreakdown = {
            transportation,
            accommodation,
            activities,
            food,
            other,
          };
          
          const perPersonTotal = Math.round(totalBudget / travelers);
          const perTravelerCosts = {
            total: perPersonTotal,
            breakdown: {
              transportation: Math.round(transportation / travelers),
              accommodation: Math.round(accommodation / travelers),
              activities: Math.round(activities / travelers),
              food: Math.round(food / travelers),
              other: Math.round(other / travelers),
            },
          };
          
          return {
            ...rec,
            costBreakdown,
            perTravelerCosts,
            numberOfTravelers: travelers,
          };
        }
        
        return rec;
      });
      
      const limited = maxItems ? recommendationsWithBreakdown.slice(0, maxItems) : recommendationsWithBreakdown;
      setRecommendations(limited);
      setLoading(false);
    });

    return () => {
      clearTimeout(loadingWatchdog);
      if (__DEV__) {
        console.log('[SavedRecommendationsList] Cleaning up subscription');
      }
      unsubscribe();
    };
  }, [userId, maxItems]);

  const computeFlexibleStartDate = (durationInDays?: number): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Set dates 3-6 months in the future (90-180 days)
    const duration = durationInDays && durationInDays > 0 ? durationInDays : 7;
    // Use 4.5 months (135 days) as base, adjust slightly for longer trips
    const baseOffset = duration >= 10 ? 150 : 120; // longer trips -> a bit further out

    const start = new Date(today);
    start.setDate(start.getDate() + baseOffset);

    const dayOfWeek = start.getDay();
    const daysUntilMonday = (8 - dayOfWeek) % 7;
    start.setDate(start.getDate() + daysUntilMonday);

    return start;
  };

  const createTripFromRecommendation = async (recommendation: SavedRecommendation, startDate: Date, endDate: Date) => {
    if (!userId) return;

    setCreatingTrip(recommendation.id);
    try {
      if (!isPremium && canCreateNewActiveTrip === false) {
        openPremiumGate({
          feature: 'trip_limit',
          title: '1 active trip on Free',
          message:
            'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
          source: 'feature_gate',
        });
        return;
      }

      // Templates are stored inside the same collection; creating a trip from a template
      // should route through the template flow (handled by the parent screen).
      if (recommendation.source === 'template' && recommendation.templateId) {
        onSelectRecommendation?.(recommendation);
        return;
      }

      if (onCreateTripRecommendation) {
        const createdTripId = await onCreateTripRecommendation(recommendation, startDate, endDate);
        if (!createdTripId) {
          return;
        }
        try {
          await unsaveRecommendation(userId, recommendation.id);
        } catch (unsaveError) {
          console.warn('Failed to remove saved recommendation after creating trip:', unsaveError);
        }
        return;
      }

      const recommendationOrigin =
        typeof (recommendation as Record<string, unknown>).travelingFrom === 'string'
          ? ((recommendation as Record<string, unknown>).travelingFrom as string).trim()
          : undefined;

      const tripPayload: CreateTripData = {
        name: `${recommendation.destination} Trip`,
        destination: recommendation.destination,
        startDate,
        endDate,
        travelMode: 'mixed',
        emailSyncEnabled: false,
      };

      const profile = useUserStore.getState().profile;
      const inferredOrigin = recommendationOrigin || profile.homeCountry || profile.citizenship || undefined;
      if (inferredOrigin) {
        tripPayload.travelingFrom = inferredOrigin;
      }

      await createTrip(userId, tripPayload);

      Alert.alert(
        'Trip Created!',
        `Your ${recommendation.destination} trip has been created successfully. You can edit the dates from the trip details if needed.`
      );
      try {
        await unsaveRecommendation(userId, recommendation.id);
      } catch (unsaveError) {
        console.warn('Failed to remove saved recommendation after creating trip:', unsaveError);
      }
    } catch (error) {
      console.error('Failed to create trip from recommendation:', error);
      Alert.alert('Error', 'Failed to create trip. Please try again.');
    } finally {
      setCreatingTrip(null);
    }
  };

  const handleCreateTrip = async (recommendation: SavedRecommendation) => {
    if (!userId) return;

    // Always create trip immediately with computed best dates
    const duration = recommendation.duration && recommendation.duration > 0 ? recommendation.duration : 7;
    const startDate = computeFlexibleStartDate(duration);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + duration);

    await createTripFromRecommendation(recommendation, startDate, endDate);
  };

  const handleRemove = async (recommendation: SavedRecommendation) => {
    const displayTitle = recommendation.title || recommendation.destination;
    Alert.alert(
      copyVariant === 'places' ? 'Remove from Bucket List' : 'Remove recommendation',
      copyVariant === 'places'
        ? `Remove "${displayTitle}" from your Bucket List?`
        : `Are you sure you want to remove "${displayTitle}" from your saved recommendations?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: copyVariant === 'places' ? 'Remove' : 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              await unsaveRecommendation(userId, recommendation.id);
            } catch (error) {
              console.error('Failed to remove recommendation:', error);
              Alert.alert('Error', 'Failed to remove recommendation. Please try again.');
            }
          },
        },
      ]
    );
  };

  const handleShare = (recommendation: SavedRecommendation) => {
    setSelectedRecommendation(recommendation);
    setShareModalVisible(true);
  };

  if (loading) {
    return (
      <View style={{ padding: theme.spacing(2) }}>
        <Body color={theme.colors.textSecondary}>Loading saved recommendations...</Body>
      </View>
    );
  }

  if (recommendations.length === 0) {
    const showEmptyCtas = typeof onEmptyAskAi === 'function' || typeof onEmptyBrowseTemplates === 'function';
    const stack = Platform.OS !== 'web' || width < 420;
    return (
      <View style={{ padding: theme.spacing(2), paddingTop: 0 }}>
        <Body color={theme.colors.textSecondary} style={{ lineHeight: 20 }}>
          {copyVariant === 'places'
            ? 'Your Bucket List is empty. Save destinations you want to visit, then come back here to turn them into a trip.'
            : 'No saved recommendations yet. Get personalized trip ideas from Tripvio AI or browse curated templates to get started.'}
        </Body>
        {showEmptyCtas && (
          <View
            style={{
              marginTop: theme.spacing(2),
              gap: theme.spacing(1),
              flexDirection: stack ? 'column' : 'row',
            }}
          >
            {typeof onEmptyAskAi === 'function' && (
              <Button
                label="Ask AI"
                onPress={onEmptyAskAi}
                icon={<Ionicons name="sparkles" size={18} color={theme.colors.surface} />}
                style={{ flex: stack ? 0 : 1, width: stack ? '100%' : undefined }}
                labelNumberOfLines={1}
              />
            )}
            {typeof onEmptyBrowseTemplates === 'function' && (
              <Button
                label="Browse Templates"
                variant="secondary"
                onPress={onEmptyBrowseTemplates}
                icon={<Ionicons name="map-outline" size={16} color={theme.colors.primary} />}
                style={{ flex: stack ? 0 : 1, width: stack ? '100%' : undefined }}
                labelNumberOfLines={1}
              />
            )}
          </View>
        )}
      </View>
    );
  }

  return (
    <>
      {layout === 'horizontal' ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{
            gap: theme.spacing(1),
            paddingRight: theme.spacing(1),
          }}
        >
          {recommendations.map((recommendation) => (
            <CompactRecommendationCard
              key={recommendation.id}
              recommendation={recommendation}
              theme={theme}
              userId={userId}
              onSelectRecommendation={onSelectRecommendation}
              showCreateTripButton={showCreateTripButton}
              showShareButton={showShareButton}
              showRemoveButton={showRemoveButton}
              onRemove={handleRemove}
              onCreateTrip={handleCreateTrip}
              onShare={handleShare}
              creatingTrip={creatingTrip}
            />
          ))}
        </ScrollView>
      ) : (
        <View style={{ gap: theme.spacing(2) }}>
          {recommendations.map((recommendation) => {
            return (
              <RecommendationCard
                key={recommendation.id}
                recommendation={recommendation}
                theme={theme}
                userId={userId}
                onSelectRecommendation={onSelectRecommendation}
                showCreateTripButton={showCreateTripButton}
                showShareButton={showShareButton}
                showRemoveButton={showRemoveButton}
                onRemove={handleRemove}
                onCreateTrip={handleCreateTrip}
                creatingTrip={creatingTrip}
              />
            );
          })}
        </View>
      )}

      {selectedRecommendation && (
        <ShareRecommendationModal
          visible={shareModalVisible}
          onClose={() => {
            setShareModalVisible(false);
            setSelectedRecommendation(null);
          }}
          userId={userId}
          recommendation={selectedRecommendation}
        />
      )}

    </>
  );
}

